import http from '../services/httpService';
import abpUserConfigurationService from '../services/abpUserConfigurationService';
import Utils from './utils';
import { normalizeRoleList } from './authUtils';
import { applyGrantedPoliciesToAbp, isAnonymousZeroConfig } from './abpAuthCookie';

declare var abp: any;

export interface HostCurrentUser {
  id?: string;
  roles: string[];
  isAuthenticated: boolean;
  grantedPolicies: Record<string, boolean>;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function asPolicyMap(value: any): Record<string, boolean> {
  if (!value || typeof value !== 'object') {
    return {};
  }
  return Object.keys(value).reduce((acc, key) => {
    if (value[key]) {
      acc[key] = true;
    }
    return acc;
  }, {} as Record<string, boolean>);
}

/** Zero GetAll → abp.auth.grantedPermissions. Skip anonymous payloads when a token is already set. */
export async function refreshAbpAuthFromHost(): Promise<boolean> {
  const data = await abpUserConfigurationService.getAll();
  const result = data?.data?.result;
  if (!result) {
    return false;
  }
  if (abp.auth.getToken() && isAnonymousZeroConfig(result)) {
    return false;
  }
  Utils.extend(true, abp, result);
  return !isAnonymousZeroConfig(result);
}

export async function fetchHostCurrentUser(): Promise<HostCurrentUser> {
  const res = await http.get('/api/abp/application-configuration');
  const body = res.data?.currentUser ? res.data : res.data?.result || res.data || {};
  const currentUser = body.currentUser || {};
  const grantedPolicies = asPolicyMap(body.auth?.grantedPolicies);
  const roles = normalizeRoleList(currentUser.roles);
  const isAuthenticated = currentUser.isAuthenticated === true || !!(currentUser.id && roles.length);
  return {
    id: currentUser.id != null ? String(currentUser.id) : undefined,
    roles,
    isAuthenticated,
    grantedPolicies,
  };
}

function hostAuthReady(appliedZero: boolean, host: HostCurrentUser): boolean {
  return (
    appliedZero ||
    host.isAuthenticated ||
    host.roles.length > 0 ||
    Object.keys(host.grantedPolicies).length > 0
  );
}

/**
 * After Abp.AuthToken is set: Zero GetAll (isGranted) + ABP.io application-configuration
 * (currentUser.roles / grantedPolicies). Retries. Does not clear the cookie.
 */
export async function ensureHostAuthAfterSignIn(retries: number = 2): Promise<HostCurrentUser> {
  let lastError: unknown = new Error('Host application-configuration is unavailable.');
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const appliedZero = await refreshAbpAuthFromHost();
      const host = await fetchHostCurrentUser();
      applyGrantedPoliciesToAbp(host.grantedPolicies);
      if (hostAuthReady(appliedZero, host)) {
        return host;
      }
      lastError = new Error('Host returned anonymous application-configuration.');
    } catch (error) {
      lastError = error;
    }
    if (attempt < retries) {
      await delay(300 * (attempt + 1));
    }
  }
  throw lastError;
}

export const hydrateAbpAuthFromHost = ensureHostAuthAfterSignIn;
